import SiteMessagesInstance from '../../components/site-messages'
import loadingSpinner from './components/loading-spinner'
import { signalLabel, signalBars } from '../../util/ble-signal'
import CacheInstance from '../../components/cache'
import { isBetaSite } from '../../util/beta-site'

// Which phone the app is on, for the wording of the permission help: the
// setting has a different name and place on each. The WebView's user agent
// still says which OS, even now that it hides the version.
function appPlatform() {

  const ua = navigator.userAgent || '';

  if (/android/i.test(ua)) {
    return 'android';
  }

  if (/iphone|ipad|ipod|ios/i.test(ua)) {
    return 'ios';
  }

  return 'other';

}

// Android's major version from the device info the app reports at startup
// (main.js caches it as 'device'), or null when unknown. Android 12 gave
// Bluetooth a permission of its own; before that scanning runs on Location,
// which is what the customer is asked for and has to find in settings.
function androidVersion() {

  try {

    const cached = CacheInstance.get('device');
    const info = cached && cached.value ? cached.value : cached;
    const major = parseInt(String((info && info.osVersion) || ''), 10);

    return isNaN(major) ? null : major;

  } catch (e) {
    return null;
  }

}

// The connection step. Bluetooth is the first-class path — for v3 it is the
// one that can't go wrong — so it comes first in every form the bridge can
// take, and the rest falls in behind it:
//
//   live     the app: a live list of nearby Temp Sticks in setup mode, one
//            tap connects. The QR scan picks one of them by id.
//   chooser  a browser with Web Bluetooth: a button that opens the browser's
//            own device picker.
//   none     no Bluetooth at all (older app build, ?transport=wifi): the QR
//            first screen exactly as before.
//
// Wi-Fi is the fallback, never the pitch: a QR scan that finds no Bluetooth
// sensor with that id (a Temp Stick without Bluetooth) goes on to the Sensor
// Setup network by itself, and "Connect Manually" stays at the bottom for
// the same sensors.
//
// A sensor only advertises when it is brand new or the customer put it in
// setup mode, so everything listed is something they mean to set up.
export class StepStartApp {
  constructor (onboarding) {
    this.onboarding = onboarding;
  }

  id = "start_app";
  title = "Connect to Temp Stick";

  // How long a sensor stays listed after its last advertisement was heard.
  device_ttl_ms = 12000;

  template = `
  <div class="onboarding-step" data-for="start_app">

    <!-- Beta testers: why this browser gets no Bluetooth option -->
    <div class="onboarding-ble-note" style="display: none;"></div>

    <!-- Bluetooth, the app: live discovery -->
    <div class="onboarding-ble-nearby" style="display: none;">

      <p class="onboarding-guide-instruction text--semibold medium-text-center">
        Nearby Temp Sticks
      </p>

      <p class="onboarding-guide-instruction-smaller medium-text-center">
        Put your Temp Stick in setup mode and it will appear here. Tap it to connect.
      </p>

      <div class="onboarding-ble-nearby__list"></div>

      <div class="onboarding-ble-nearby__state"></div>

      <!-- NIST calibration account only: the whole list in one run -->
      <div class="onboarding-ble-nearby__bulk" style="display: none;">
        <button type="button" class="button button--medium" data-for="bulk_setup"></button>
        <div class="onboarding-guide-instruction-smaller medium-text-center">
          Recalibration batch: every sensor listed joins the calibration Wi-Fi and is named after its ID.
        </div>
      </div>

    </div>

    <!-- Bluetooth, a browser: the browser's own picker -->
    <div class="onboarding-ble-chooser" style="display: none;">

      <p class="onboarding-guide-instruction text--semibold medium-text-center">
        Connect over Bluetooth
      </p>

      <p class="onboarding-guide-instruction-smaller medium-text-center">
        Put your Temp Stick in setup mode, then pick it from the list your browser shows.
      </p>

      <div class="onboarding-scan-qr-code">
        <button type="button" class="button button--medium" data-for="ble_chooser">
          Connect with Bluetooth
        </button>
      </div>

    </div>

    <!-- QR code: with Bluetooth, the exact pick; without it, the old first step -->
    <div class="onboarding-start-app__qr">

      <p class="onboarding-guide-instruction text--semibold medium-text-center" data-for="qr_lead">
        Automatically connect by scanning the <b>QR code</b> on your Temp Stick.
      </p>

      <div class="onboarding-scan-qr-code">
        <button type="button" class="button scan-qr-code-btn" data-for="scan_qr_code">
          <div class="scan-qr-code-btn__icon">
            <img src="${window.site.home}/../assets/images/setup/icon-qr-code.png" width="25" />
          </div>
          <div class="scan-qr-code-btn__label">
            Scan QR Code
          </div>
        </button>
      </div>

      <div class="onboarding-guide-instruction-smaller medium-text-center">
        Your QR code and Sensor ID are located on the inside of your battery cover.
      </div>
      <div class="onboarding-guide-instruction-smaller text--blue medium-text-center">
        <b>Note:</b> hold your camera 3-6 inches from QR code in a well-lit room for best results.
      </div>

    </div>

    <div class="onboarding-simulate medium-text-center" style="display:none; margin-top: 12px; margin-bottom: 15px;">
      <button type="button" class="button button--light-gray" data-for="simulate_onboarding">
        Simulate Onboarding
      </button>
    </div>

    <div class="onboarding-simulate medium-text-center" style="display:none; margin-top: 12px; margin-bottom: 15px;">
      <button type="button" class="button button--light-gray" data-for="simulate_selective">
        Simulate Selective
      </button>
    </div>

    <div class="text-divider">
      <div class="text-divider__text">OR</div>
    </div>

    <p class="onboarding-guide-instruction-smaller medium-text-center" data-for="manual_lead">
      Don't have a QR code or the scanner isn't working?
    </p>

    <button type="button" class="button button--light-gray" data-for="connect_manually">
      Connect Manually
    </button>

  </div>
  `;

  onBack = () => {
    this.onboarding.goToStep('step_setup_wifi_password');
  }

  // Which form Bluetooth takes here: 'live', 'chooser' or 'none'.
  bleMode() {

    if (this.onboarding.transportPreference() === 'wifi') {
      return 'none';
    }

    const bridge = this.onboarding.bleBridge();

    if (!bridge) {
      return 'none';
    }

    if (bridge.chooser) {
      return 'chooser';
    }

    return (typeof bridge.startScan === 'function') ? 'live' : 'none';

  }

  hasQrScanner() {
    return !!(window.gonative && window.gonative.barcode && typeof window.gonative.barcode.scan === 'function');
  }

  onActivate = () => {
    this.onboarding.showBackButton();

    this.onboarding.selected_wifi_ssid = null;
    this.onboarding.wifi_networks = {};

    // Simulate buttons are for testers; they never show on temperaturestick.com.
    $(this.onboarding.container).find('.onboarding-simulate').toggle(isBetaSite());

    this.mode = this.bleMode();
    this.live = this.mode === 'live';

    this.$nearby.toggle(this.mode === 'live');
    this.$chooser.toggle(this.mode === 'chooser');
    this.$chooserBtn.prop('disabled', false).text('Connect with Bluetooth');

    const note = this.mode === 'none' ? this.onboarding.webBleUnavailableNote() : '';
    this.$bleNote.html(note).toggle(note.length > 0);

    // With Bluetooth in front, the QR code is how you single one out; on a
    // browser there's no camera bridge, so the block only shows if there is.
    const showQr = this.mode === 'none' || this.hasQrScanner();
    this.$qr.toggle(showQr);
    this.$qrLead.html(this.mode === 'none'
      ? 'Automatically connect by scanning the <b>QR code</b> on your Temp Stick.'
      : 'Setting up more than one? Scan the <b>QR code</b> on the one in your hand to pick it.');

    this.$manualLead.html(this.mode === 'none'
      ? "Don't have a QR code or the scanner isn't working?"
      : 'Temp Sticks without Bluetooth connect over their Sensor Setup Wi-Fi network instead.');

    if (this.live) {
      this.beginNearbyDiscovery();
    }
  }

  onDeactivate = () => {
    this.stopNearbyScan();
    this.awaiting_settings = false;
    this.away_in_settings = false;
  }

  bindActions = () => {

    const $step = $(this.onboarding.container).find('.onboarding-step[data-for="start_app"]');

    this.$QRCodeBtn = $step.find('button[data-for="scan_qr_code"]');
    this.$connectManuallyBtn = $step.find('button[data-for="connect_manually"]');
    this.$simulateBtn = $step.find('button[data-for="simulate_onboarding"]');
    this.$simulateSelectiveBtn = $step.find('button[data-for="simulate_selective"]');
    this.$qr = $step.find('.onboarding-start-app__qr');
    this.$qrLead = $step.find('[data-for="qr_lead"]');
    this.$manualLead = $step.find('[data-for="manual_lead"]');

    this.$nearby = $step.find('.onboarding-ble-nearby');
    this.$nearbyList = $step.find('.onboarding-ble-nearby__list');
    this.$nearbyState = $step.find('.onboarding-ble-nearby__state');

    this.$chooser = $step.find('.onboarding-ble-chooser');
    this.$chooserBtn = $step.find('button[data-for="ble_chooser"]');
    this.$bleNote = $step.find('.onboarding-ble-note');
    this.$bulk = $step.find('.onboarding-ble-nearby__bulk');
    this.$bulkBtn = $step.find('button[data-for="bulk_setup"]');

    this.$bulkBtn.on('click', () => this.startBulk());

    this.devices = {};
    this.scanning = false;
    this.connecting = false;
    this.mode = 'none';

    // What this page has learnt about Bluetooth permission that the phone
    // won't repeat back: asked here and refused, and whether asking again
    // shows anything at all.
    this.permission_refused = false;
    this.permission_wont_ask = false;

    // Sent to the phone's settings from here, and been away since.
    this.awaiting_settings = false;
    this.away_in_settings = false;

    document.addEventListener('visibilitychange', () => this.onAppVisibility(document.visibilityState !== 'hidden'));
    window.addEventListener('blur', () => this.onAppVisibility(false));
    window.addEventListener('focus', () => this.onAppVisibility(true));

    window.addEventListener('tempstick:ble-device', e => this.onDeviceSeen((e && e.detail) || {}));
    window.addEventListener('tempstick:ble-scan-stopped', e => this.onScanStopped((e && e.detail) || {}));

    this.$nearbyList.on('click', '.onboarding-ble-nearby__device', e => {
      const el = e.currentTarget;
      this.connectTo(el.dataset.address, el.dataset.sensorId || '');
    });

    this.$nearbyState.on('click', 'button[data-action]', e => {
      this.onStateAction(e.currentTarget.dataset.action);
    });

    // Direct click handler on purpose: the browser only opens its picker
    // inside a user gesture.
    this.$chooserBtn.on('click', () => this.connectChooser());

    this.$QRCodeBtn.on('click', () => {

      if (this.onboarding.is_simulation()) {
        this.onboarding.simulateConnection();
        return;
      }

      if (!this.hasQrScanner()) {
        SiteMessagesInstance.addMessage('error', 'Failed to activate QR code scanner, check camera permission settings');
        SiteMessagesInstance.displayMessages();
        return;
      }

      // The scanner takes over the screen; pause discovery meanwhile.
      this.stopNearbyScan();

      window.gonative.barcode.scan({

        callback: data => {

          if (data.success) {

            let code = data.code;
            code = code.replace(/[^a-z0-9]+/gi, '');

            this.onboarding.sensorId = code;

            if (typeof this.onboarding.is_simulation === "function" && this.onboarding.is_simulation()) {
              this.onboarding.simulateConnection();
              return;
            }

            this.connectScanned(code);

          } else {

            SiteMessagesInstance.addMessage(
              "error",
              "Unable to scan the QR Code, try again or try connecting to the Temp Stick manually."
            );
            SiteMessagesInstance.displayMessages();

            this.onboarding.goToStep('start_app');

          }

        }
      });

    });

    this.$connectManuallyBtn.on('click', () => {
      this.onboarding.goToStep('connect_manually');
    });

    this.$simulateSelectiveBtn.on('click', () => {

      if (isBetaSite()) {
        this.onboarding.simulateSelective();
        this.onboarding.simulateConnection();
      }
    });

    this.$simulateBtn.on('click', () => {

      if (isBetaSite()) {
        this.onboarding.enableSimulation();
        this.onboarding.simulateConnection();
      }
    });

  }

  // A scanned QR code selects a sensor. If Bluetooth has already heard it,
  // connect to it straight away; otherwise attemptToConnect() looks for it
  // by id over Bluetooth and, failing that, joins its Wi-Fi network — the
  // one route for Temp Sticks without Bluetooth.
  connectScanned(code) {

    const wanted = String(code || '').toUpperCase();

    if (this.live) {
      const seen = Object.values(this.devices).find(device => String(device.sensorId).toUpperCase() === wanted);
      if (seen) {
        this.connectTo(seen.address, seen.sensorId);
        return;
      }
    }

    this.onboarding.attemptToConnect();

  }

  // ── Bluetooth through the browser's picker ─────────────────────────────────

  async connectChooser() {

    this.$chooserBtn.prop('disabled', true).text('Choose your Temp Stick...');

    const res = await this.onboarding.Temp_Stick_Connect_Ble({
      sensorId: this.onboarding.sensorId || '',
      timeoutMs: 20000
    });

    this.$chooserBtn.prop('disabled', false).text('Connect with Bluetooth');

    if (!res.success) {

      if (res.code === 'CANCELLED') {
        return;
      }

      SiteMessagesInstance.addMessage('error', this.failureMessage(res, this.onboarding.sensorId));
      SiteMessagesInstance.displayMessages();
      return;

    }

    this.onboarding.sensorId = res.sensorId || this.onboarding.sensorId;
    this.onboarding.manual_connect = false;
    this.onboarding.goToStep('attempt_to_connect');

  }

  // ── Live discovery ─────────────────────────────────────────────────────────

  // Bluetooth permission is asked for on this step and nowhere else — it is
  // the step that needs it — and never silently. The first visit explains
  // why and waits for a tap; the phone's own permission prompt follows that
  // tap. isAvailable() reports without asking (both apps guarantee that), so
  // looking is free: scanning is what asks.
  //
  // iOS says through `authorization` when the permission has been refused
  // for good. Android doesn't — its bridge only says permitted or not — so
  // what this page learnt from its own asking (onPermissionRefused) stands
  // in for that.
  async beginNearbyDiscovery() {

    const info = await this.onboarding.Temp_Stick_Ble_Availability();

    if (!this.live || this.onboarding.current_step !== this.id) {
      return;
    }

    if (info.permitted === false) {

      // Refused in the phone's settings, or asking here has already shown
      // nothing: only the settings page can turn it on now.
      if (info.authorization === 'denied' || info.authorization === 'restricted' || this.permission_wont_ask) {
        this.renderState('permission-denied');
        return;
      }

      // Asked here and refused, but the phone will ask once more.
      if (this.permission_refused) {
        this.renderState('permission');
        return;
      }

      this.renderState('permission-ask');
      return;

    }

    this.permission_refused = false;
    this.permission_wont_ask = false;
    this.startNearbyScan();

  }

  async startNearbyScan() {

    if (!this.live || this.connecting) {
      return;
    }

    // Any search means they're not off in the settings any more.
    this.awaiting_settings = false;
    this.away_in_settings = false;

    this.devices = {};
    this.scanning = true;
    this.renderDevices();
    this.renderState('scanning');

    clearInterval(this.pruneTimer);
    this.pruneTimer = setInterval(() => this.pruneDevices(), 2000);

    const askedAt = Date.now();
    const res = await this.onboarding.Temp_Stick_Ble_Start_Scan({ timeoutMs: 120000 });

    if (res.success) {
      this.permission_refused = false;
      this.permission_wont_ask = false;
      return;
    }

    this.scanning = false;
    clearInterval(this.pruneTimer);

    switch (res.code) {
      case 'BT_OFF':
        this.renderState('bt-off');
        break;
      case 'PERMISSION':
        this.onPermissionRefused(Date.now() - askedAt);
        break;
      case 'LOCATION_OFF':
        this.renderState('location-off');
        break;
      case 'BUSY':
        // A previous scan is still winding down; try once more shortly.
        setTimeout(() => {
          if (this.onboarding.current_step === this.id) {
            this.startNearbyScan();
          }
        }, 1200);
        break;
      default:
        // No usable Bluetooth on this device: back to the QR-first screen.
        this.live = false;
        this.mode = 'none';
        this.$nearby.hide();
        this.$qr.show();
        this.$qrLead.html('Automatically connect by scanning the <b>QR code</b> on your Temp Stick.');
        this.$manualLead.html("Don't have a QR code or the scanner isn't working?");
        break;
    }

  }

  // The bridge asked for Bluetooth permission and was refused. Once the
  // customer has refused twice, or turned it off in settings, Android says
  // no straight away without showing anything — and, unlike iOS, its
  // isAvailable() can't say so afterwards. So the answer's timing stands in:
  // inside a second, nobody was asked and only the settings page can help;
  // slower, they saw the prompt and the phone will show it once more.
  async onPermissionRefused(elapsedMs) {

    this.permission_refused = true;

    const info = await this.onboarding.Temp_Stick_Ble_Availability();

    if (!this.live || this.onboarding.current_step !== this.id) {
      return;
    }

    if (info.permitted === true) {
      this.startNearbyScan();
      return;
    }

    const wontAsk = info.authorization === 'denied'
      || info.authorization === 'restricted'
      || appPlatform() === 'ios'   // iOS asks once, ever
      || elapsedMs < 1000;

    if (wontAsk) {
      this.permission_wont_ask = true;
    }

    this.renderState(wontAsk ? 'permission-denied' : 'permission');

  }

  // ── Permission help, in the phone's own words ──────────────────────────────

  canOpenSettings() {
    return !!(window.gonative && window.gonative.run && typeof window.gonative.run.openSettings === 'function');
  }

  // Android before 12 has no Bluetooth permission of its own: scanning runs
  // on Location, and that's the prompt and the setting the customer sees.
  androidAsksForLocation() {

    const version = androidVersion();

    return appPlatform() === 'android' && version !== null && version < 12;

  }

  // What to pick on the phone's own prompt.
  promptChoice() {

    if (this.androidAsksForLocation()) {
      return 'choose <b>While using the app</b> when your phone asks for your location — this version of Android needs it to see Bluetooth devices nearby';
    }

    return 'choose <b>Allow</b> when your phone asks';

  }

  // The way to the permission from where "Open App Settings" lands: the
  // app's own page in the phone's settings. Without that button, from the
  // Settings app itself.
  settingsDirections() {

    const opener = this.canOpenSettings()
      ? "open the app's settings"
      : "open your phone's Settings app and find Temp Stick";

    switch (appPlatform()) {

      case 'android': {

        if (this.androidAsksForLocation()) {
          return `${opener}, tap <b>Permissions</b>, then <b>Location</b>, and allow it while using the app`;
        }

        // Version unknown (an older app build): name both.
        const aside = androidVersion() === null ? ' (<b>Location</b> on Android 11 and older)' : '';

        return `${opener}, tap <b>Permissions</b>, then <b>Nearby devices</b>${aside}, and choose <b>Allow</b>`;

      }

      case 'ios':
        return `${opener} and turn on <b>Bluetooth</b>`;

      default:
        return `${opener} and allow Bluetooth for the Temp Stick app`;

    }

  }

  settingsButton(style) {

    if (!this.canOpenSettings()) {
      return '';
    }

    return `<button type="button" class="button ${style}" data-action="settings">Open App Settings</button>`;

  }

  // ── Back from the phone's settings ─────────────────────────────────────────

  // Android answers openSettings' callback at once (the settings screen
  // opens in its own task), iOS on the way back, so neither is relied on:
  // the page going away and coming back is what says they've returned.
  onAppVisibility(visible) {

    if (!this.awaiting_settings) {
      return;
    }

    if (!visible) {
      this.away_in_settings = true;
      return;
    }

    if (this.away_in_settings) {
      this.resumeAfterSettings();
    }

  }

  // Look again without asking: permission granted over there starts the
  // search by itself, still off shows the directions again.
  resumeAfterSettings() {

    this.awaiting_settings = false;
    this.away_in_settings = false;

    if (!this.live || this.onboarding.current_step !== this.id) {
      return;
    }

    this.beginNearbyDiscovery();

  }

  stopNearbyScan() {

    clearInterval(this.pruneTimer);

    if (!this.scanning) {
      return;
    }

    this.scanning = false;
    this.onboarding.Temp_Stick_Ble_Stop_Scan();

  }

  onDeviceSeen(detail) {

    if (!this.live || !this.scanning || this.onboarding.current_step !== this.id) {
      return;
    }

    const address = String(detail.address || '');

    if (!address) {
      return;
    }

    const rssi = parseInt(detail.rssi, 10);
    const existing = this.devices[address];

    this.devices[address] = {
      address,
      name: String(detail.name || ''),
      sensorId: String(detail.sensorId || ''),
      // Smoothed so the order doesn't jump around between advertisements.
      rssi: (existing && !isNaN(existing.rssi) && !isNaN(rssi)) ? Math.round(existing.rssi * 0.6 + rssi * 0.4) : rssi,
      lastSeen: Date.now()
    };

    this.renderDevices();

  }

  onScanStopped(detail) {

    if (!this.live || this.onboarding.current_step !== this.id) {
      return;
    }

    if (detail.reason === 'timeout' && this.scanning && !this.connecting) {
      this.scanning = false;
      clearInterval(this.pruneTimer);
      this.renderState('timeout');
    }

  }

  pruneDevices() {

    const cutoff = Date.now() - this.device_ttl_ms;
    let changed = false;

    Object.keys(this.devices).forEach(address => {
      if (this.devices[address].lastSeen < cutoff) {
        delete this.devices[address];
        changed = true;
      }
    });

    if (changed) {
      this.renderDevices();
    }

  }

  // Closest first.
  sortedDevices() {
    return Object.values(this.devices)
      .sort((a, b) => (b.rssi || -999) - (a.rssi || -999));
  }

  // The calibration lab's batch button: everything currently listed, one run.
  renderBulk(devices) {

    const offer = this.live && !this.connecting && devices.length > 0 && this.onboarding.canBulkOnboard();

    this.$bulk.toggle(offer);

    if (offer) {
      this.$bulkBtn.text(`Set up all ${devices.length} for recalibration`);
    }

  }

  startBulk() {

    const devices = this.sortedDevices();

    if (devices.length === 0 || !this.onboarding.canBulkOnboard()) {
      return;
    }

    this.stopNearbyScan();
    this.onboarding.startBulk(devices.map(device => ({
      address: device.address,
      sensorId: device.sensorId,
      name: device.name
    })));

  }

  renderDevices() {

    const devices = this.sortedDevices();

    this.renderBulk(devices);

    if (devices.length === 0) {
      this.$nearbyList.html('');
      return;
    }

    // One sensor: it's theirs, say so. Several: point out the one clearly
    // nearest, when the signal gap is big enough to mean anything.
    const single = devices.length === 1;
    const closest = devices.length > 1 && (devices[0].rssi - devices[1].rssi) >= 8 ? devices[0].address : null;

    this.$nearbyList.html(devices.map(device => {

      const label = device.sensorId ? `Temp Stick ${device.sensorId}` : (device.name || 'Temp Stick');
      const classes = ['onboarding-ble-nearby__device'];
      let badge = '';

      if (single) {
        classes.push('is-primary');
        badge = 'Found';
      } else if (device.address === closest) {
        classes.push('is-primary');
        badge = 'Closest';
      }

      return `
        <button type="button" class="${classes.join(' ')}" data-address="${device.address}" data-sensor-id="${device.sensorId}" ${this.connecting ? 'disabled' : ''}>
          <div class="onboarding-ble-nearby__device-body">
            <div class="onboarding-ble-nearby__device-name">${label}</div>
            <div class="onboarding-ble-nearby__device-meta">
              <span class="onboarding-ble-nearby__bars" data-bars="${signalBars(device.rssi)}"><span></span><span></span><span></span></span>${signalLabel(device.rssi)}
            </div>
          </div>
          ${badge ? `<span class="onboarding-ble-nearby__device-badge">${badge}</span>` : ''}
        </button>
      `;

    }).join(''));

  }

  renderState(state) {

    let html = '';

    switch (state) {
      case 'scanning':
        html = `${loadingSpinner()}<span>Looking for Temp Sticks in setup mode...</span>`;
        break;
      case 'connecting':
        html = `${loadingSpinner()}<span>Connecting...</span>`;
        break;
      case 'timeout':
        html = `<span>Stopped looking. Make sure the sensor's light is blinking, then search again.</span>
                <button type="button" class="button button--light-gray" data-action="rescan">Search Again</button>`;
        break;
      case 'bt-off':
        html = `<span>Bluetooth is turned off.</span>
                <button type="button" class="button button--light-gray" data-action="enable">Turn on Bluetooth</button>`;
        break;
      case 'permission-ask':
        html = `<span>The app uses Bluetooth to find your Temp Stick and send it your Wi-Fi details. Tap below, then ${this.promptChoice()}.</span>
                <button type="button" class="button button--medium" data-action="rescan">Allow Bluetooth</button>`;
        break;
      case 'permission':
        // Refused on the prompt; the phone will show it once more.
        html = `<span>Bluetooth permission was turned down, so the app can't look for your Temp Stick. Tap Try Again and ${this.promptChoice()}. If your phone doesn't ask, ${this.settingsDirections()}.</span>
                <button type="button" class="button button--medium" data-action="rescan">Try Again</button>
                ${this.settingsButton('button--light-gray')}`;
        break;
      case 'permission-denied':
        // Asking shows nothing any more; only the settings page can turn it on.
        html = `<span>Bluetooth permission is turned off for the Temp Stick app, so it can't look for your Temp Stick. To turn it on, ${this.settingsDirections()}. Then come back here.</span>
                ${this.settingsButton('button--medium')}
                <button type="button" class="button button--light-gray" data-action="rescan">Try Again</button>`;
        break;
      case 'location-off':
        html = `<span>Turn on Location on this device — this version of Android needs it to scan for Bluetooth sensors.</span>
                <button type="button" class="button button--light-gray" data-action="rescan">Try Again</button>`;
        break;
      default:
        html = '';
        break;
    }

    this.$nearbyState.html(html);

  }

  async onStateAction(action) {

    if (action === 'rescan') {
      this.startNearbyScan();
      return;
    }

    if (action === 'enable') {
      const bridge = this.onboarding.bleBridge();
      if (bridge && typeof bridge.enable === 'function') {
        this.renderState('scanning');
        const askedAt = Date.now();
        const res = await bridge.enable({});
        if (res && res.enabled) {
          this.startNearbyScan();
        } else if (res && res.code === 'PERMISSION') {
          // Turning Bluetooth on needs the permission first, and that was refused.
          this.onPermissionRefused(Date.now() - askedAt);
        } else {
          this.renderState('bt-off');
        }
      }
      return;
    }

    if (action === 'settings') {

      if (!this.canOpenSettings()) {
        return;
      }

      this.awaiting_settings = true;
      this.away_in_settings = false;

      // The callback only counts once the page has actually been away; on
      // Android it fires straight away, before they've left (onAppVisibility).
      window.gonative.run.openSettings({ callback: () => this.onAppVisibility(document.visibilityState !== 'hidden') });
      return;

    }

  }

  async connectTo(address, sensorId) {

    if (this.connecting) {
      return;
    }

    this.connecting = true;
    this.stopNearbyScan();
    this.renderDevices();
    this.$nearbyList.find(`.onboarding-ble-nearby__device[data-address="${address}"]`).addClass('is-active');
    this.renderState('connecting');

    const res = await this.onboarding.Temp_Stick_Connect_Ble({ address, sensorId, timeoutMs: 15000 });

    this.connecting = false;

    if (!res.success) {

      SiteMessagesInstance.addMessage('error', this.failureMessage(res, sensorId));
      SiteMessagesInstance.displayMessages();

      if (this.onboarding.current_step === this.id) {
        this.startNearbyScan();
      }

      return;

    }

    // From here it is the same as after a QR scan: the connecting step's
    // heartbeat identifies the sensor over the new link and routes onward.
    this.onboarding.sensorId = res.sensorId || sensorId;
    this.onboarding.manual_connect = false;
    this.onboarding.goToStep('attempt_to_connect');

  }

  failureMessage(res, sensorId) {

    switch (res && res.code) {
      case 'BT_OFF':
        return 'Bluetooth is turned off on this device. Turn it on and try again.';
      case 'PERMISSION':
        return `The app doesn't have Bluetooth permission: ${this.settingsDirections().replace(/<\/?b>/g, '')}, then try again.`;
      case 'UNAVAILABLE':
        return "This device can't use Bluetooth to set up a Temp Stick. Use the Sensor Setup Wi-Fi network instead.";
      case 'WRONG_SENSOR':
        return 'That turned out to be a different Temp Stick. Pick it from the list again.';
      case 'TIMEOUT':
      case 'NOT_FOUND':
        return `We couldn't reach Temp Stick ${sensorId || ''}. Make sure its light is still blinking and it's close by, then tap it again.`;
      default:
        return (res && res.message) ? res.message : "We couldn't connect over Bluetooth, please try again.";
    }

  }

}
